import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {DateInputCalendarSelector} from '@Components/date-input-calendar-selector';
import {TimeInput} from '@Components/time-input';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import type {DateTimeSelectorProps} from './date-time-selector.types';
import styles from './date-time-selector.module.scss';
export function DateTimeSelector({
  value,
  onChange,
  minDate,
  timeDropdownId,
  calendarAlignment = PopoverDefaultAlignment.BOTTOM_CENTER,
  className = '',
  showAiHighlight = false,
}: DateTimeSelectorProps): ReactElement {
  const [pulseKey, setPulseKey] = useState<number>(0);
  const [isPulsing, setIsPulsing] = useState<boolean>(false);
  const wasHighlightActive = useRef<boolean>(false);

  useEffect(() => {
    if (showAiHighlight && !wasHighlightActive.current) {
      setPulseKey((key) => key + 1);
      setIsPulsing(true);
    } else if (!showAiHighlight && wasHighlightActive.current) {
      setIsPulsing(false);
    }

    wasHighlightActive.current = showAiHighlight;
  }, [showAiHighlight]);

  const handleDateSelected = (date: Date): void => {
    const updated = new Date(value);
    updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    onChange(updated);
  };

  const handleTimeUpdated = (hour: number, minute: number): void => {
    const updated = new Date(value);
    updated.setHours(hour, minute, 0, 0);
    onChange(updated);
  };

  const handleTimeSelected = (hour: number, minute: number, isAmPmToggle: boolean, isFromDropdown: boolean): void => {
    if (isAmPmToggle || isFromDropdown) {
      handleTimeUpdated(hour, minute);
    }
  };

  const renderAiBorderOverlay = (): ReactElement | null => {
    if (!isPulsing) {
      return null;
    }

    return (
      <span
        key={pulseKey}
        className={styles.aiBorderOverlay}
        onAnimationEnd={(): void => {
          setIsPulsing(false);
        }}
      />
    );
  };

  return (
    <div className={`${styles.selector} ${className}`}>
      <div className={styles.fieldWrapper}>
        <DateInputCalendarSelector
          className={`${styles.dateTimeField} flex-row-align-center`}
          selectedDate={value}
          minDate={minDate}
          onSelectDate={handleDateSelected}
          alignment={calendarAlignment}
          popoverClassName={styles.calendarPopover}
        />
        {renderAiBorderOverlay()}
      </div>
      <div className={styles.fieldWrapper}>
        <TimeInput
          className={styles.dateTimeField}
          isFullWidth
          dropdownID={timeDropdownId}
          defaultSelectedDateTime={value}
          minSelectableDateTime={minDate}
          onSelectTime={handleTimeSelected}
          onTimeInputBlurCallback={handleTimeUpdated}
        />
        {renderAiBorderOverlay()}
      </div>
    </div>
  );
}
